import { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Upload, CheckCircle, XCircle, AlertTriangle, Mail, UserPlus } from 'lucide-react';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import { createEmail } from '../../../services/emailServiceSwitch';
import { addEmailsToGroup } from '../../../services/groupServiceSwitch';
import { parseEmailCsv } from '../../../utils/csvHelpers';
import '../EmailManagement/EmailManagement.css';

/**
 * ImportGroupEmailsModal
 *
 * Imports emails from CSV into a group:
 * 1. Parse CSV → email/name pairs
 * 2. For each email: resolve to existing ID or create new (triggers SES)
 * 3. Filter out emails already in the group
 * 4. Batch-add remaining to group
 * 5. Show summary
 */
export default function ImportGroupEmailsModal({ isOpen, onClose, onSuccess, groupId, groupEmails, availableEmails }) {
  const fileInputRef = useRef(null);

  const [step, setStep] = useState('upload'); // upload | preview | importing | done
  const [parsedRows, setParsedRows] = useState([]);
  const [parseErrors, setParseErrors] = useState([]);
  const [progress, setProgress] = useState({ current: 0, total: 0, phase: '' });
  const [results, setResults] = useState({ added: 0, skipped: 0, created: 0, failed: 0, errors: [] });

  const reset = () => {
    setStep('upload');
    setParsedRows([]);
    setParseErrors([]);
    setProgress({ current: 0, total: 0, phase: '' });
    setResults({ added: 0, skipped: 0, created: 0, failed: 0, errors: [] });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    if (step === 'importing') return;
    reset();
    onClose();
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const { data, errors } = parseEmailCsv(evt.target.result);
      setParsedRows(data);
      setParseErrors(errors);
      setStep('preview');
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    setStep('importing');
    const total = parsedRows.length;
    const res = { added: 0, skipped: 0, created: 0, failed: 0, errors: [] };

    // Build lookup of existing emails (lowercase → id)
    const existingMap = new Map();
    (availableEmails || []).forEach(e => existingMap.set(e.email.toLowerCase(), e.id));

    // Build set of emails already in this group
    const inGroupSet = new Set((groupEmails || []).map(e => e.email.toLowerCase()));

    // Phase 1: Resolve email IDs (create if needed)
    setProgress({ current: 0, total, phase: 'Resolving emails' });
    const emailIdsToAdd = [];

    for (let i = 0; i < total; i++) {
      const row = parsedRows[i];
      const emailLower = row.email.toLowerCase();

      // Already in group → skip
      if (inGroupSet.has(emailLower)) {
        res.skipped++;
        setProgress({ current: i + 1, total, phase: 'Resolving emails' });
        continue;
      }

      // Existing email → use its ID
      if (existingMap.has(emailLower)) {
        emailIdsToAdd.push(existingMap.get(emailLower));
        setProgress({ current: i + 1, total, phase: 'Resolving emails' });
        continue;
      }

      // New email → create it (triggers SES verification)
      try {
        const created = await createEmail({ email: row.email, name: row.name });
        const newId = created.id || created.data?.id;
        if (newId) {
          emailIdsToAdd.push(newId);
          existingMap.set(emailLower, newId);
          res.created++;
        } else {
          res.failed++;
          res.errors.push(`Row ${i + 1}: No ID returned from create`);
        }
      } catch (err) {
        const errData = err?.response?.data?.error || err?.response?.data || {};
        const code = errData.code || '';
        const msg = errData.message || err.message || 'Unknown error';
        // If it already exists, try to find its ID from the error or skip
        if (code === 'DUPLICATE_EMAIL' || msg.includes('already exists')) {
          // It exists but wasn't in our loaded list — skip adding to group since we can't get the ID
          res.skipped++;
        } else {
          res.failed++;
          res.errors.push(`Row ${i + 1}: ${msg}`);
        }
      }
      setProgress({ current: i + 1, total, phase: 'Resolving emails' });
    }

    // Phase 2: Batch add to group
    if (emailIdsToAdd.length > 0) {
      setProgress({ current: 0, total: 1, phase: 'Adding to group' });
      try {
        await addEmailsToGroup(groupId, emailIdsToAdd);
        res.added = emailIdsToAdd.length;
      } catch (err) {
        const msg = err?.response?.data?.error?.message || err.message || 'Failed to add emails to group';
        res.failed += emailIdsToAdd.length;
        res.added = 0;
        res.errors.push(msg);
      }
      setProgress({ current: 1, total: 1, phase: 'Adding to group' });
    }

    setResults(res);
    setStep('done');
  };

  const handleDone = () => {
    const msg = `Import complete: ${results.added} added, ${results.skipped} skipped, ${results.created} new emails created`;
    if (onSuccess) onSuccess(msg);
    reset();
    onClose();
  };

  // --- Render ---

  const renderUpload = () => (
    <div className="import-upload-area">
      <div className="upload-icon"><Upload size={40} /></div>
      <p className="upload-text">Select a CSV file with an <code>email</code> column (and optional <code>name</code>)</p>
      <p className="upload-text" style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
        New emails will be created and SES verification sent automatically
      </p>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv"
        onChange={handleFileSelect}
        className="file-input"
        aria-label="Select CSV file"
      />
      <Button variant="secondary" onClick={() => fileInputRef.current?.click()}>
        Choose File
      </Button>
    </div>
  );

  const renderPreview = () => {
    const inGroupSet = new Set((groupEmails || []).map(e => e.email.toLowerCase()));
    const existingSet = new Set((availableEmails || []).map(e => e.email.toLowerCase()));
    const newCount = parsedRows.filter(r => !existingSet.has(r.email.toLowerCase())).length;
    const alreadyInGroup = parsedRows.filter(r => inGroupSet.has(r.email.toLowerCase())).length;
    const toAdd = parsedRows.length - alreadyInGroup;

    return (
      <div className="import-preview">
        {parseErrors.length > 0 && (
          <div className="import-warnings">
            <AlertTriangle size={16} />
            <div>{parseErrors.map((e, i) => <div key={i} className="warning-line">{e}</div>)}</div>
          </div>
        )}
        <p className="preview-count">{parsedRows.length} email(s) found</p>
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.75rem', fontSize: '0.8rem' }}>
          <span style={{ color: 'var(--color-success)' }}>
            <UserPlus size={14} style={{ verticalAlign: 'middle' }} /> {toAdd} to add
          </span>
          <span style={{ color: 'var(--warning-color)' }}>
            <AlertTriangle size={14} style={{ verticalAlign: 'middle' }} /> {alreadyInGroup} already in group
          </span>
          {newCount > 0 && (
            <span style={{ color: 'var(--color-primary-400)' }}>
              <Mail size={14} style={{ verticalAlign: 'middle' }} /> {newCount} new (SES verification)
            </span>
          )}
        </div>
        <div className="preview-table-wrap">
          <table className="preview-table">
            <thead><tr><th>Email</th><th>Name</th><th>Status</th></tr></thead>
            <tbody>
              {parsedRows.slice(0, 50).map((r, i) => {
                const inGroup = inGroupSet.has(r.email.toLowerCase());
                const isNew = !existingSet.has(r.email.toLowerCase());
                return (
                  <tr key={i}>
                    <td>{r.email}</td>
                    <td>{r.name || '—'}</td>
                    <td style={{ fontSize: '0.75rem' }}>
                      {inGroup ? <span style={{ color: 'var(--warning-color)' }}>Already in group</span>
                        : isNew ? <span style={{ color: 'var(--color-primary-400)' }}>New — will create</span>
                        : <span style={{ color: 'var(--color-success)' }}>Will add</span>}
                    </td>
                  </tr>
                );
              })}
              {parsedRows.length > 50 && (
                <tr><td colSpan={3} className="more-rows">…and {parsedRows.length - 50} more</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderImporting = () => {
    const pct = progress.total ? Math.round((progress.current / progress.total) * 100) : 0;
    return (
      <div className="import-progress">
        <p>{progress.phase}: {progress.current} of {progress.total}</p>
        <div className="progress-bar-track">
          <div className="progress-bar-fill" style={{ width: `${pct}%` }} />
        </div>
        <p className="progress-pct">{pct}%</p>
      </div>
    );
  };

  const renderDone = () => (
    <div className="import-results">
      <div className="result-row result-success"><CheckCircle size={18} /> {results.added} added to group</div>
      <div className="result-row result-skipped"><AlertTriangle size={18} /> {results.skipped} already in group (skipped)</div>
      {results.created > 0 && (
        <div className="result-row" style={{ color: 'var(--color-primary-400)' }}>
          <Mail size={18} /> {results.created} new emails created (SES verification sent)
        </div>
      )}
      {results.failed > 0 && (
        <div className="result-row result-failed"><XCircle size={18} /> {results.failed} failed</div>
      )}
      {results.errors.length > 0 && (
        <div className="import-error-list">
          {results.errors.slice(0, 10).map((e, i) => <div key={i} className="error-line">{e}</div>)}
        </div>
      )}
    </div>
  );

  const footerByStep = {
    upload: null,
    preview: (
      <div className="modal-actions">
        <Button variant="secondary" onClick={reset}>Back</Button>
        <Button variant="primary" onClick={handleImport} disabled={parsedRows.length === 0} icon={<Upload size={18} />}>
          Import {parsedRows.length} Email{parsedRows.length !== 1 ? 's' : ''}
        </Button>
      </div>
    ),
    importing: null,
    done: (
      <div className="modal-actions">
        <Button variant="primary" onClick={handleDone}>Done</Button>
      </div>
    )
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Import Emails to Group"
      size="md"
      closeOnBackdrop={step !== 'importing'}
      footer={footerByStep[step]}
    >
      {step === 'upload' && renderUpload()}
      {step === 'preview' && renderPreview()}
      {step === 'importing' && renderImporting()}
      {step === 'done' && renderDone()}
    </Modal>
  );
}

ImportGroupEmailsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  groupId: PropTypes.string.isRequired,
  groupEmails: PropTypes.array,
  availableEmails: PropTypes.array
};
